import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useOverlayOpen } from '@/lib/overlay';
import { usePurchaseBarOpen } from '@/lib/purchaseBar';
import { isPrivatePath } from '@/lib/privateRoutes';

/**
 * GOHIGHLEVEL CHAT — LAZY LOADER + LAUNCHER GATE
 *
 * 1. LAZY LOAD (2026-10-11 performance pass). The LeadConnector loader used to
 *    be a <script> in index.html, so every visitor paid for the largest
 *    third-party script on first paint. It is now injected from here, ONCE per
 *    page session, on the first of:
 *      - a real user interaction (pointerdown, keydown, touchstart, scroll,
 *        wheel), or
 *      - 5 seconds after a public page mounts.
 *    It is never armed on private routes (dashboard, admin, checkout-style
 *    pages); if a visitor lands on one first, loading waits until they reach a
 *    public page. The loader itself handles late insertion (it checks
 *    document.readyState and reads its data-* attributes from currentScript).
 *
 * 2. VISIBILITY GATE (unchanged). This component renders nothing; it toggles
 *    `gg-chat-hidden` on <html> and src/index.css hides the widget while that
 *    class is present — on private routes, while any registered overlay is
 *    open, or while the sticky lead purchase bar is on screen.
 *
 * It never opens, closes or reconfigures the widget and touches no checkout,
 * payment or registration logic.
 */
const HIDDEN_CLASS = 'gg-chat-hidden';
const LOADER_SRC = 'https://widgets.leadconnectorhq.com/loader.js';
const LOADER_ATTRS: Record<string, string> = {
  'data-resources-url': 'https://widgets.leadconnectorhq.com/chat-widget/loader.js',
  'data-widget-id': '6ac9157c0e60a54e322b7808',
  'data-source': 'WEB_USER',
};
const IDLE_DELAY_MS = 5000;
const TRIGGER_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'scroll', 'wheel'] as const;

let injected = false;

function injectChatLoader() {
  if (injected || typeof document === 'undefined') return;
  injected = true;
  if (document.querySelector(`script[src="${LOADER_SRC}"]`)) return;
  const s = document.createElement('script');
  s.src = LOADER_SRC;
  s.async = true;
  Object.entries(LOADER_ATTRS).forEach(([k, v]) => s.setAttribute(k, v));
  document.body.appendChild(s);
}

const ChatWidgetGate: React.FC = () => {
  const { pathname } = useLocation();
  const overlayOpen = useOverlayOpen();
  const purchaseBar = usePurchaseBarOpen();
  const privateRoute = isPrivatePath(pathname);

  const hidden = privateRoute || overlayOpen || purchaseBar;

  // Arm the lazy loader on public routes only.
  useEffect(() => {
    if (injected || privateRoute) return;
    const opts: AddEventListenerOptions = { passive: true, capture: true };
    const fire = () => {
      cleanup();
      injectChatLoader();
    };
    const timer = window.setTimeout(fire, IDLE_DELAY_MS);
    function cleanup() {
      window.clearTimeout(timer);
      TRIGGER_EVENTS.forEach((ev) => window.removeEventListener(ev, fire, opts));
    }
    TRIGGER_EVENTS.forEach((ev) => window.addEventListener(ev, fire, opts));
    return cleanup;
  }, [privateRoute]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle(HIDDEN_CLASS, hidden);
  }, [hidden]);

  // Never leave the launcher hidden if the shell unmounts.
  useEffect(() => () => document.documentElement.classList.remove(HIDDEN_CLASS), []);

  return null;
};

export default ChatWidgetGate;
